import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { CheckCircle, Clock, User, Phone, Package, Palette, AlertCircle } from 'lucide-react';
import { apiService } from '../services/api';

export const FilaEsperaPage = () => {
  const [loading, setLoading] = useState(false);
  const [sucesso, setSucesso] = useState(false);
  const [erro, setErro] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    telefone: '',
    plano_interesse: 'fidelidade',
    cor_preferencia: 'Preta',
    observacoes: ''
  });

  const planos = [
    { value: 'fidelidade', label: 'Plano Fidelidade - R$ 425,00/semana (Tudo incluso + Moto Zero Km!)' },
    { value: 'anual', label: 'Plano Anual - R$ 349,99/semana (Carência 1 ano - Troca de óleo inclusa)' },
    { value: 'mensal', label: 'Plano Mensal - R$ 400,00/semana (Carência 3 meses - Troca de óleo inclusa)' }
  ];

  const cores = ['Preta', 'Vermelha', 'Azul'];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErro(null);

    try {
      const response = await apiService.cadastrarFilaEspera(formData);
      setSucesso(true);
      
      // Limpar formulário
      setFormData({
        nome: '',
        telefone: '',
        plano_interesse: 'fidelidade',
        cor_preferencia: 'Preta',
        observacoes: ''
      });
    } catch (error) {
      setErro(error.response?.data?.detail || 'Erro ao cadastrar na fila de espera');
    } finally {
      setLoading(false);
    }
  };

  if (sucesso) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50 flex items-center justify-center p-4">
        <Card className="max-w-2xl w-full">
          <CardHeader className="text-center">
            <div className="mx-auto w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <CardTitle className="text-3xl text-green-600">
              Cadastro Realizado com Sucesso!
            </CardTitle>
            <CardDescription className="text-lg mt-4">
              Você foi adicionado à nossa fila de espera
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h3 className="font-semibold text-blue-900 mb-3">📱 O que acontece agora?</h3>
              <ul className="space-y-2 text-blue-800">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <span>Entraremos em contato assim que novas motos chegarem à nossa frota</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <span>Você terá prioridade no agendamento</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
                  <span>Não se preocupe, não há compromisso até você confirmar</span>
                </li>
              </ul>
            </div>

            <Alert className="bg-yellow-50 border-yellow-200">
              <AlertCircle className="h-4 w-4 text-yellow-600" />
              <AlertDescription className="text-yellow-800">
                <strong>Importante:</strong> Mantenha seu telefone atualizado e responda nossos contatos o mais rápido possível para não perder a oportunidade!
              </AlertDescription>
            </Alert>

            <div className="flex gap-4">
              <Button 
                onClick={() => setSucesso(false)} 
                variant="outline"
                className="flex-1"
              >
                Fazer Novo Cadastro
              </Button>
              <Button 
                onClick={() => window.location.href = '/'} 
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                Voltar ao Início
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <div className="inline-block p-3 bg-blue-100 rounded-full mb-4">
            <Clock className="w-12 h-12 text-blue-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Fila de Espera
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            No momento não temos motos disponíveis, mas você pode entrar na nossa fila de espera e garantir prioridade!
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">Cadastre-se na Fila de Espera</CardTitle>
            <CardDescription>
              Preencha os dados abaixo e entraremos em contato assim que tivermos motos disponíveis
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Nome */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <User className="w-4 h-4" />
                  Nome Completo *
                </label>
                <input
                  type="text"
                  name="nome"
                  value={formData.nome}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Seu nome completo"
                />
              </div>

              {/* Telefone */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <Phone className="w-4 h-4" />
                  Telefone/WhatsApp *
                </label>
                <input
                  type="tel"
                  name="telefone"
                  value={formData.telefone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="(11) 98765-4321"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Entraremos em contato por este número quando motos estiverem disponíveis
                </p>
              </div>

              {/* Plano de Interesse */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <Package className="w-4 h-4" />
                  Plano de Interesse *
                </label>
                <select
                  name="plano_interesse"
                  value={formData.plano_interesse}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {planos.map(plano => (
                    <option key={plano.value} value={plano.value}>
                      {plano.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Cor Preferida */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
                  <Palette className="w-4 h-4" />
                  Cor Preferida da Moto
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {cores.map(cor => (
                    <button
                      key={cor}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, cor_preferencia: cor }))}
                      className={`
                        px-4 py-3 rounded-lg border-2 transition-all
                        ${formData.cor_preferencia === cor
                          ? 'border-blue-500 bg-blue-50 text-blue-700 font-semibold'
                          : 'border-gray-300 hover:border-gray-400'
                        }
                      `}
                    >
                      {cor}
                    </button>
                  ))}
                </div>
              </div>

              {/* Observações */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Observações (Opcional)
                </label>
                <textarea
                  name="observacoes"
                  value={formData.observacoes}
                  onChange={handleChange}
                  rows="3"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder="Alguma informação adicional que gostaria de compartilhar?"
                />
              </div>

              {/* Erro */}
              {erro && (
                <Alert className="bg-red-50 border-red-200">
                  <AlertCircle className="h-4 w-4 text-red-600" />
                  <AlertDescription className="text-red-800">
                    {erro}
                  </AlertDescription>
                </Alert>
              )}

              {/* Informações */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-2">ℹ️ Informações Importantes</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Não há compromisso ao se cadastrar na fila</li>
                  <li>• Você pode desistir a qualquer momento</li>
                  <li>• O cadastro é totalmente gratuito</li>
                  <li>• Entraremos em contato apenas quando houver disponibilidade</li>
                </ul>
              </div>

              {/* Botão Submit */}
              <Button
                type="submit"
                disabled={loading}
                className="w-full py-6 text-lg bg-blue-600 hover:bg-blue-700"
              >
                {loading ? (
                  <>
                    <Clock className="w-5 h-5 mr-2 animate-spin" />
                    Cadastrando...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5 mr-2" />
                    Entrar na Fila de Espera
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* CTA Alternativo */}
        <div className="mt-8 text-center">
          <p className="text-gray-600 mb-4">
            Quer saber mais sobre nossos planos antes de se cadastrar?
          </p>
          <Button 
            variant="outline"
            onClick={() => window.location.href = '/planos'}
            className="mr-4"
          >
            Ver Planos
          </Button>
          <Button 
            variant="outline"
            onClick={() => window.location.href = '/contato'}
          >
            Falar Conosco
          </Button>
        </div>
      </div>
    </div>
  );
};
